import React, { useState, useEffect } from 'react';
import { 
  getCustomCollections,
  createCustomCollection,
  updateCustomCollection,
  deleteCustomCollection,
  toggleCustomCollectionAvailability
} from '../services/profileRingService';

function AdminCollectionManager({ isOpen, onClose, adminUserId }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' or 'create'
  
  // New collection creation form
  const [newCollection, setNewCollection] = useState({
    name: '',
    description: '',
    icon: '💍',
    key: '',
    color: '#3B82F6',
    isActive: true
  });
  const [creating, setCreating] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);

  // Common icon options
  const iconOptions = [
    '💍', '✨', '🌟', '💎', '🔮', '🌙', '☀️', '⚡', '🔥', '💫',
    '🌈', '🌺', '🌸', '🌿', '🍃', '🌊', '❄️', '🌀', '👑', '🗡️',
    '🛡️', '🏹', '🎯', '🎨', '🎭', '🎪', '🎨', '🎰', '🎲', '🃏'
  ];

  useEffect(() => {
    if (!isOpen) return;

    const loadCollections = async () => {
      setLoading(true);
      try {
        const customCollections = await getCustomCollections();
        setCollections(customCollections);
      } catch (error) {
        console.error('Error loading collections:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCollections();
  }, [isOpen]);

  const generateCollectionKey = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 20);
  };

  const handleCreateCollection = async (e) => {
    e.preventDefault();
    
    if (!newCollection.name.trim() || !newCollection.description.trim()) {
      alert('Please fill in name and description');
      return;
    }
    
    const collectionKey = newCollection.key.trim() || generateCollectionKey(newCollection.name);
    
    // Check for duplicate keys
    if (collections.some(col => col.key === collectionKey)) {
      alert('Collection key already exists. Please use a different name or specify a custom key.');
      return;
    }
    
    setCreating(true);
    try {
      const collectionData = {
        name: newCollection.name.trim(),
        description: newCollection.description.trim(),
        icon: newCollection.icon,
        key: collectionKey,
        color: newCollection.color,
        isActive: newCollection.isActive,
        createdBy: adminUserId,
        createdAt: Date.now()
      };
      
      const collectionId = await createCustomCollection(collectionData);
      
      if (collectionId) {
        // Add to local state
        setCollections(prev => [...prev, { id: collectionId, ...collectionData }]);
        
        // Reset form
        setNewCollection({
          name: '',
          description: '',
          icon: '💍',
          key: '',
          color: '#3B82F6',
          isActive: true
        });
        
        alert('Collection created successfully!');
        setActiveTab('manage');
      } else {
        alert('Failed to create collection: Database operation failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error creating collection:', error);
      alert(`Failed to create collection: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCollection = async (e) => {
    e.preventDefault();
    
    if (!editingCollection || !editingCollection.name.trim()) {
      return;
    }
    
    try {
      const success = await updateCustomCollection(editingCollection.id, {
        name: editingCollection.name.trim(),
        description: editingCollection.description.trim(),
        icon: editingCollection.icon,
        color: editingCollection.color,
        isActive: editingCollection.isActive
      });
      
      if (success) {
        // Update local state
        setCollections(prev => prev.map(col => 
          col.id === editingCollection.id 
            ? { ...col, ...editingCollection }
            : col
        ));
        
        setEditingCollection(null);
        alert('Collection updated successfully!');
      } else {
        alert('Failed to update collection');
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      alert(`Failed to update collection: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection? This cannot be undone.')) {
      return;
    }
    
    try {
      const success = await deleteCustomCollection(collectionId);
      if (success) {
        setCollections(prev => prev.filter(col => col.id !== collectionId));
        alert('Collection deleted successfully');
      } else {
        alert('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      alert(`Failed to delete collection: ${error.message || 'Unknown error'}`);
    }
  };

  const handleToggleAvailability = async (collectionId, currentStatus) => {
    try {
      const success = await toggleCustomCollectionAvailability(collectionId, !currentStatus);
      if (success) {
        setCollections(prev => prev.map(col => 
          col.id === collectionId 
            ? { ...col, isActive: !currentStatus }
            : col
        ));
      }
    } catch (error) {
      console.error('Error toggling collection availability:', error);
      alert('Failed to update collection availability');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-collection-modal-overlay" onClick={onClose}>
      <div className="admin-collection-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="admin-collection-modal-header">
          <h2>Collection Manager</h2>
          <button className="admin-collection-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="admin-ring-tabs">
          <button 
            className={`tab-button ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Collections
          </button>
          <button 
            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Collection
          </button>
        </div>

        {loading ? (
          <div className="admin-ring-loading">
            <div className="emoji-loader">
              <span className="emoji-item">📚</span>
              <span className="emoji-item">💍</span>
              <span className="emoji-item">⚡</span>
            </div>
            <p>Loading collections...</p>
          </div>
        ) : (
          <>
            {/* Manage Collections Tab */}
            {activeTab === 'manage' && (
              <div className="manage-collections-section">
                <h3>Custom Collections</h3>
                
                {collections.length > 0 ? (
                  <div className="collections-grid">
                    {collections.map(collection => (
                      <div key={collection.id} className={`collection-item ${!collection.isActive ? 'inactive' : ''}`}>
                        <div className="collection-header">
                          <div className="collection-icon" style={{ color: collection.color }}>
                            {collection.icon}
                          </div>
                          <div className="collection-info">
                            <div className="collection-name">{collection.name}</div>
                            <div className="collection-key">Key: {collection.key}</div>
                          </div>
                        </div>
                        
                        <div className="collection-description">{collection.description}</div>
                        
                        <div className="collection-meta">
                          <span className="collection-status" style={{ 
                            color: collection.isActive ? '#10B981' : '#EF4444' 
                          }}>
                            {collection.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="collection-actions">
                          <button 
                            className="edit-collection-button"
                            onClick={() => setEditingCollection({...collection})}
                          >
                            Edit
                          </button>
                          <button 
                            className={`toggle-collection-button ${collection.isActive ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleAvailability(collection.id, collection.isActive)}
                          >
                            {collection.isActive ? 'Disable' : 'Enable'}
                          </button>
                          <button 
                            className="delete-collection-button"
                            onClick={() => handleDeleteCollection(collection.id)}
                          >
                            Delete
                          </button>
                        </div>
                        
                        {!collection.isActive && (
                          <div className="collection-inactive-overlay">
                            <span>Disabled</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-collections">
                    <div className="no-collections-icon">📚</div>
                    <div className="no-collections-text">
                      No custom collections created yet.
                      <br />
                      Create your first collection to get started!
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Create Collection Tab */}
            {activeTab === 'create' && (
              <div className="create-collection-section">
                <h3>Create New Collection</h3>
                
                <div className="create-collection-layout">
                  <div className="collection-form-container">
                    <form onSubmit={handleCreateCollection} className="create-collection-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>Collection Name</label>
                          <input
                            type="text"
                            value={newCollection.name}
                            onChange={(e) => {
                              const name = e.target.value;
                              setNewCollection({
                                ...newCollection, 
                                name: name,
                                key: newCollection.key || generateCollectionKey(name)
                              });
                            }}
                            placeholder="Enter collection name"
                            maxLength={50}
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label>Collection Key</label>
                          <input
                            type="text"
                            value={newCollection.key}
                            onChange={(e) => setNewCollection({...newCollection, key: e.target.value})}
                            placeholder="auto_generated_from_name"
                            maxLength={20}
                          />
                          <div className="form-help">
                            Used internally. Leave empty to auto-generate from name.
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={newCollection.description}
                          onChange={(e) => setNewCollection({...newCollection, description: e.target.value})}
                          placeholder="Describe this collection"
                          maxLength={200}
                          rows={3}
                          required
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Icon</label>
                          <div className="icon-selector">
                            <div className="selected-icon" style={{ color: newCollection.color }}>
                              {newCollection.icon}
                            </div>
                            <div className="icon-options">
                              {iconOptions.map(icon => (
                                <button 
                                  key={icon}
                                  type="button"
                                  className={`icon-option ${newCollection.icon === icon ? 'selected' : ''}`}
                                  onClick={() => setNewCollection({...newCollection, icon: icon})}
                                >
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="form-group">
                          <label>Color</label>
                          <input
                            type="color"
                            value={newCollection.color}
                            onChange={(e) => setNewCollection({...newCollection, color: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={newCollection.isActive}
                            onChange={(e) => setNewCollection({...newCollection, isActive: e.target.checked})}
                          />
                          Active (available for use)
                        </label>
                      </div>
                      
                      <button 
                        type="submit" 
                        className="create-collection-button"
                        disabled={creating}
                      >
                        {creating ? 'Creating...' : 'Create Collection'}
                      </button>
                    </form>
                  </div>
                  
                  {/* Preview */}
                  <div className="collection-preview-container">
                    <h4>Preview</h4>
                    <div className="collection-preview">
                      <div className="preview-icon" style={{ color: newCollection.color }}>
                        {newCollection.icon}
                      </div>
                      <div className="preview-name">{newCollection.name || 'Collection Name'}</div>
                      <div className="preview-description">{newCollection.description || 'Collection description...'}</div>
                      <div className="preview-key">Key: {newCollection.key || 'auto_generated'}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Collection Modal */}
            {editingCollection && (
              <div className="edit-collection-modal">
                <div className="edit-collection-content">
                  <h3>Edit Collection</h3>
                  <form onSubmit={handleUpdateCollection} className="edit-collection-form">
                    <div className="form-group">
                      <label>Collection Name</label>
                      <input
                        type="text"
                        value={editingCollection.name}
                        onChange={(e) => setEditingCollection({...editingCollection, name: e.target.value})}
                        maxLength={50}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={editingCollection.description}
                        onChange={(e) => setEditingCollection({...editingCollection, description: e.target.value})}
                        maxLength={200}
                        rows={3}
                        required
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Icon</label>
                        <div className="icon-selector">
                          <div className="selected-icon" style={{ color: editingCollection.color }}>
                            {editingCollection.icon}
                          </div>
                          <div className="icon-options">
                            {iconOptions.slice(0, 15).map(icon => (
                              <button 
                                key={icon}
                                type="button"
                                className={`icon-option ${editingCollection.icon === icon ? 'selected' : ''}`}
                                onClick={() => setEditingCollection({...editingCollection, icon: icon})}
                              >
                                {icon}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Color</label>
                        <input
                          type="color"
                          value={editingCollection.color}
                          onChange={(e) => setEditingCollection({...editingCollection, color: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="form-buttons">
                      <button type="button" onClick={() => setEditingCollection(null)} className="cancel-button">
                        Cancel
                      </button>
                      <button type="submit" className="save-button">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminCollectionManager;